/**
 * SIDEBAR — Rail di navigazione del nuovo design (port dal mockup).
 * Chiuso: solo icone (64px). Hover: si espande a 252px mostrando le label.
 * L'auto-espansione è pura CSS (.railnav__panel:hover), nessuno stato JS.
 * Le voci e i link restano quelli reali (core + plugin abilitati).
 */

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEnabledPlugins } from '../plugin/useEnabledPlugins';
import { useWhatsAppEnabled } from '../entities/whatsAppMessage/useWhatsAppEnabled';
import { useDisabledCoreItems } from '../plugin/useCoreItems';
import {
  DashboardGlyph, ContactsGlyph, CompaniesGlyph, QuotationsGlyph,
  InboxGlyph, AutomationsGlyph, FairsGlyph, SettingsGlyph, FolderGlyph,
  CheckSquareGlyph, TagGlyph, LayersGlyph, BoardGlyph, DossierGlyph, EyeGlyph,
  VisitGlyph, MapGlyph, CalendarGlyph,
} from '../components/icons';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import type { MenuItem } from '../plugin/types';

// Risolve il token `icon` di una voce di menu (core o plugin) nel glifo da
// disegnare. Token sconosciuto → FolderGlyph (stesso disaccoppiamento di
// `automationIcons.actionIcon`: un plugin dichiara la propria icona senza che
// il core la conosca).
//
// I token coperti sono quelli dichiarati dalle voci core e dai plugin: il
// restyle cambia il glifo, non l'insieme dei token, altrimenti una voce
// esistente scivolerebbe in silenzio sul fallback.
const NAV_ICON: Record<string, ReactNode> = {
  dashboard: <DashboardGlyph />,
  task: <CheckSquareGlyph />,
  person: <ContactsGlyph />,
  business: <CompaniesGlyph />,
  receipt: <QuotationsGlyph />,
  kanban: <BoardGlyph />,
  pipeline: <LayersGlyph />,
  category: <TagGlyph />,
  email: <InboxGlyph />,
  autorenew: <AutomationsGlyph />,
  // Lo stesso marchio del pulsante WhatsApp della scheda contatto.
  whatsapp: <WhatsAppIcon />,
  rapidContact: <FairsGlyph />,
  map: <MapGlyph />,
  settings: <SettingsGlyph />,
  // Token di dominio dichiarati dai plugin. Stanno qui e non nel plugin perche'
  // il glifo appartiene al design system: il plugin nomina l'icona, non la
  // disegna. Il token porta lo stesso nome del glifo — `dossier` e non `case` —
  // perche' due nomi per un disegno solo si perdono di vista a vicenda.
  dossier: <DossierGlyph />,
  surveillance: <EyeGlyph />,
  visit: <VisitGlyph />,
  // L'agenda della settimana di F2 (FILO-41).
  calendar: <CalendarGlyph />,
};

function navIcon(token?: string): ReactNode {
  return (token && NAV_ICON[token]) || <FolderGlyph />;
}

const coreMenuItems: MenuItem[] = [
  // La lista task è raggiunta dal logo della topbar (rotta home): qui omessa.
  { key: 'dashboard', label: 'nav.dashboard', path: '/dashboard', icon: 'dashboard', order: 5 },
  { key: 'contacts', label: 'nav.contacts', path: '/contacts', icon: 'person', order: 10 },
  { key: 'companies', label: 'nav.companies', path: '/companies', icon: 'business', order: 15 },
  { key: 'map', label: 'nav.map', path: '/map', icon: 'map', order: 16 },
  { key: 'quotations', label: 'nav.quotations', path: '/quotations', icon: 'receipt', order: 20 },
  // La configurazione Pipeline è passata sotto Impostazioni (SettingsPage,
  // sezione 'pipelines'): non è più una voce del menu principale (TS-101).
  { key: 'kanban', label: 'nav.kanban', path: '/kanban', icon: 'kanban', order: 23 },
  { key: 'inbox', label: 'nav.inbox', path: '/inbox', icon: 'email', order: 25 },
  { key: 'surveillance', label: 'nav.surveillance', path: '/surveillance', icon: 'surveillance', order: 26 },
  { key: 'automations', label: 'nav.automations', path: '/automations', icon: 'autorenew', order: 27 },
  { key: 'whatsapp', label: 'nav.whatsapp', path: '/whatsapp', icon: 'whatsapp', order: 28 },
  { key: 'rapid-contacts', label: 'nav.rapidContacts', path: '/rapid-contacts', icon: 'rapidContact', order: 30 },
  { key: 'settings', label: 'nav.settings', path: '/settings', icon: 'settings', order: 35 },
];

export function Sidebar() {
  const { t } = useTranslation();
  const enabledPlugins = useEnabledPlugins();
  const pluginMenuItems = enabledPlugins.flatMap((p) => p.menuItems ?? []);
  // WhatsApp solo se la company ha un numero business configurato.
  const whatsAppEnabled = useWhatsAppEnabled();
  // Le voci spente dalla pagina «Voci del CRM» (FILO-53).
  const disabledCoreItems = useDisabledCoreItems();
  const coreItems = coreMenuItems.filter(
    (i) => !disabledCoreItems.has(i.key) && (whatsAppEnabled || i.key !== 'whatsapp'),
  );
  const allItems = [...coreItems, ...pluginMenuItems].sort(
    (a, b) => (a.order ?? 100) - (b.order ?? 100),
  );

  return (
    <nav className="railnav" aria-label="Navigazione principale">
      <div className="railnav__panel">
        {allItems.map((item) => {
          const label = t(item.label);
          return (
            <NavLink
              key={item.key}
              to={item.path}
              end={item.path === '/tasks'}
              className="rn-item"
              title={label}
            >
              <span className="rn-icon">{navIcon(item.icon)}</span>
              <span className="rn-label">{label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
